import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import { getAllCategoriesMobile, getAllTeamPlayersByFridgeId } from "../services/productService";
import allProductsWhiteImage from "../assets/images/categories/all_products_white.png";
import allProductsImage from "../assets/images/categories/all_products.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faStar, faGlassWater, faCookie, faUtensils, faCoffee, faBreadSlice,
    faAppleAlt, faFish, faIceCream, faWineGlass, faCheese, faCarrot,
    faEgg, faBox, faSearch, faTimes
} from "@fortawesome/free-solid-svg-icons";

const categoryIcons = {
    "all": faStar,
    "se allt": faStar,
    "alla": faStar,
    "drinks": faGlassWater,
    "dryck": faGlassWater,
    "drycker": faGlassWater,
    "snacks": faCookie,
    "snack": faCookie,
    "meals": faUtensils,
    "måltid": faUtensils,
    "måltider": faUtensils,
    "mat": faUtensils,
    "godis": faCookie,
    "chips": faCookie,
    "kaffe": faCoffee,
    "te": faCoffee,
    "juice": faGlassWater,
    "vatten": faGlassWater,
    "öl": faGlassWater,
    "vin": faWineGlass,
    "bröd": faBreadSlice,
    "kött": faUtensils,
    "fisk": faFish,
    "grönsaker": faCarrot,
    "frukt": faAppleAlt,
    "mejeri": faCheese,
    "glass": faIceCream,
    "choklad": faCookie,
    "ägg": faEgg
};

const getIconForCategory = (name) => {
    const key = (name || "").toString().trim().toLowerCase();
    return categoryIcons[key] || faBox;
};

const HorizontalCategoryList = ({
                                    selectedFridgeId,
                                    onCategorySelect,
                                    onProductSearch,
                                    isVoucherFridge,
                                    // new: notify parent about team player selection in voucher mode
                                    onTeamPlayerSelect,
                                }) => {
    const { token } = useAuth();
    // const token ="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJtdW5pci5oYWRyb3ZpY0BnbWFpbC5jb20iLCJlbWFpbCI6Im11bmlyLmhhZHJvdmljQGdtYWlsLmNvbSIsImp0aSI6IjJkZTEzNjM2LTNkZmQtNGQwYy05MTIwLWE1NTMzMWVlMGFhZSIsInVuaXF1ZV9uYW1lIjoibXVuaXIuaGFkcm92aWNAZ21haWwuY29tIiwiY3VzdG9tZXJJZCI6IjExMiIsImV4cCI6MTc1NzE2MTMxNywiaXNzIjoiQm94bWVhbCBBQiIsImF1ZCI6ImFkbWluLmJveG1lYWwuc2UifQ.b1o4G54HFTZpj0GWLtrWo15acXA8ZdZLWiXPbOHFgBA";
    const [categories, setCategories] = useState([
        { id: "all", title: "Alla", image: null }, // force icon (faStar)
    ]);
    // const [selectedCategoryId, setSelectedCategoryId] = useState(() => sessionStorage.getItem("lastCategory") || "all");
    const [selectedCategoryId, setSelectedCategoryId] = useState("all");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI state for compact sticky + search
    const [isScrolled, setIsScrolled] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [showSearchClear, setShowSearchClear] = useState(false);
    const containerRef = useRef(null);
    const trackRef = useRef(null);
    const searchInputRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const isSearchActive = isScrolled || isSearchFocused || searchQuery.trim() !== "";

    useEffect(() => {
        if (!token) {
            setError("User not authenticated");
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                if (isVoucherFridge) {
                    if (!selectedFridgeId) {
                        setError("Fridge not selected");
                        setCategories([{ id: "all", title: "Alla", image: null }]);
                        setSelectedCategoryId("all");
                        return;
                    }
                    // Fetch team players tied to the selected fridge and customer from JWT
                    const players = await getAllTeamPlayersByFridgeId(token, selectedFridgeId);
                    if (!Array.isArray(players)) {
                        throw new Error("Invalid team players response format");
                    }

                    const mapped = players.map((p) => ({
                        id: String(p.teamPlayerId ?? p.id ?? p.userId ?? p.memberId ?? Math.random()),
                        title: String(p.name ?? p.teamPlayerName ?? p.fullName ?? p.displayName ?? "Spelare"),
                        image: p.imageUrl || p.photoUrl || null,
                    }));

                    setCategories([
                        { id: "all", title: "Alla", image: null }, // always star icon
                        ...mapped,
                    ]);
                    setSelectedCategoryId((prev) => (prev && mapped.some(m => m.id === prev) ? prev : "all"));
                } else {
                    if (!selectedFridgeId) {
                        setError("Fridge not selected");
                        setCategories([{ id: "all", title: "Alla", image: null }]);
                        setSelectedCategoryId("all");
                        return;
                    }

                    const fetchedCategories = await getAllCategoriesMobile(token, selectedFridgeId);
                    if (!Array.isArray(fetchedCategories)) {
                        throw new Error("Invalid categories response format");
                    }

                    const filteredCategories = fetchedCategories
                        .filter((cat) => (cat.categoryName || "").toLowerCase() !== "alla")
                        .map((cat) => ({
                            id: String(cat.categoryId),
                            title: cat.categoryName,
                            image: cat.imageUrl || null,
                        }));

                    setCategories([
                        { id: "all", title: "Alla", image: null }, // force star icon
                        ...filteredCategories,
                    ]);
                    setSelectedCategoryId((prev) => prev ?? "all");
                }
            } catch (err) {
                console.error("Error fetching data:", err);
                setError(isVoucherFridge ? "Failed to load team players" : "Failed to load categories");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [token, selectedFridgeId, isVoucherFridge]);

    useEffect(() => {
        const handleScroll = () => {
            const y =
                window.scrollY ||
                window.pageYOffset ||
                document.documentElement.scrollTop ||
                document.body.scrollTop ||
                0;
            setIsScrolled(y > 50);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        document.addEventListener("scroll", handleScroll, { passive: true });
        document.body.addEventListener("scroll", handleScroll, { passive: true });
        document.documentElement.addEventListener("scroll", handleScroll, { passive: true });

        handleScroll();
        return () => {
            window.removeEventListener("scroll", handleScroll);
            document.removeEventListener("scroll", handleScroll);
            document.body.removeEventListener("scroll", handleScroll);
            document.documentElement.removeEventListener("scroll", handleScroll);
        };
    }, []);


    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition =
                window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
            const shouldBeScrolled = scrollPosition > 50;

            if (shouldBeScrolled && !isScrolled && containerRef.current) {
                const track = containerRef.current.querySelector('[role="tablist"]');
                if (track) track.scrollLeft = 0;
            }
            if (shouldBeScrolled !== isScrolled) setIsScrolled(shouldBeScrolled);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, [isScrolled]);

    // const cardSize = useMemo(() => {
    //     if (!containerWidth || categories.length === 0) {
    //         return { normal: 80, compact: 50 };
    //     }
    //     const normalSize = 80;
    //     const visibleCount = Math.min(categories.length, 6);
    //     const padding = 32; // 16px each side
    //     const gaps = (visibleCount - 1) * 8;
    //     const availableWidth = containerWidth - padding - gaps;
    //     const compactSize = Math.min(70, Math.max(40, Math.floor(availableWidth / visibleCount)));
    //     return { normal: normalSize, compact: compactSize };
    // }, [containerWidth, categories.length]);

    const cardSize = useMemo(() => ({ normal: 80, compact: 80 }), []);

    const handleCategoryClick = (categoryId) => {
        setSelectedCategoryId(categoryId);
        sessionStorage.setItem("lastCategory", categoryId);
        if (isVoucherFridge) {
            // In voucher mode, clicking a player should propagate selection upward
            if (categoryId === "all") {
                onCategorySelect?.("all");
                onTeamPlayerSelect?.(null); // reset player filter
            } else {
                onTeamPlayerSelect?.(categoryId);
            }
        } else {
            onCategorySelect?.(categoryId);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        setShowSearchClear(value.length > 0);

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            onProductSearch?.(value);
        }, 300);
    };

    const clearSearch = () => {
        setSearchQuery("");
        setShowSearchClear(false);
        onProductSearch?.("");
        searchInputRef.current?.focus();
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            onProductSearch?.(searchQuery);
        }
    };

    useEffect(() => {
        const el = trackRef.current;
        if (!el) return;

        let isDown = false;
        let startX = 0;
        let scrollStart = 0;

        const getClientX = (e) => (e.touches && e.touches.length ? e.touches[0].clientX : e.clientX);

        const onDown = (e) => {
            isDown = true;
            startX = getClientX(e) || 0;
            scrollStart = el.scrollLeft;
        };

        const onMove = (e) => {
            if (!isDown) return;
            const x = getClientX(e) || 0;
            const dx = x - startX;
            el.scrollLeft = scrollStart - dx;
            e.preventDefault();
        };

        const onUp = () => {
            isDown = false;
        };

        el.addEventListener("pointerdown", onDown, { passive: true });
        el.addEventListener("pointermove", onMove, { passive: false });
        el.addEventListener("pointerup", onUp, { passive: true });
        el.addEventListener("pointerleave", onUp, { passive: true });
        el.addEventListener("touchstart", onDown, { passive: true });
        el.addEventListener("touchmove", onMove, { passive: false });
        el.addEventListener("touchend", onUp, { passive: true });

        return () => {
            el.removeEventListener("pointerdown", onDown);
            el.removeEventListener("pointermove", onMove);
            el.removeEventListener("pointerup", onUp);
            el.removeEventListener("pointerleave", onUp);
            el.removeEventListener("touchstart", onDown);
            el.removeEventListener("touchmove", onMove);
            el.removeEventListener("touchend", onUp);
        };
    }, []);

    return (
        <BarWrap ref={containerRef} $isScrolled={isScrolled}>
            {/* Categories or Team Players */}
            <Track ref={trackRef} role="tablist" aria-label={isVoucherFridge ? "Spelare" : "Kategorier"} $isScrolled={isScrolled}>
                {loading && (
                    <>
                        <Skeleton $isScrolled={isScrolled} $size={isScrolled ? cardSize.compact : cardSize.normal} />
                        <Skeleton $isScrolled={isScrolled} $size={isScrolled ? cardSize.compact : cardSize.normal} />
                        <Skeleton $isScrolled={isScrolled} $size={isScrolled ? cardSize.compact : cardSize.normal} />
                    </>
                )}

                {!loading && error && (
                    <ErrorText>{error}</ErrorText>
                )}

                {!loading && !error && categories.map((item) => (
                    <Card
                        type="button"
                        key={item.id}
                        $active={item.id === selectedCategoryId}
                        $isScrolled={isScrolled}
                        $size={cardSize.normal}
                        $isVisible={true}
                        onClick={() => handleCategoryClick(item.id)}
                        role="tab"
                        aria-selected={item.id === selectedCategoryId}
                        title={item.title}
                    >
                        <IconBox $isScrolled={isScrolled} $size={isScrolled ? cardSize.compact : cardSize.normal}>
                            {item.id === 'all' ? (
                                <IconContainer $active={item.id === selectedCategoryId} $isScrolled={isScrolled}>
                                    <FontAwesomeIcon icon={faStar} />
                                </IconContainer>
                            ) : item.image ? (
                                <IconImg src={item.image} alt="" $isScrolled={isScrolled} />
                            ) : (
                                <IconContainer $active={item.id === selectedCategoryId} $isScrolled={isScrolled}>
                                    <FontAwesomeIcon icon={getIconForCategory(item.title)} />
                                </IconContainer>
                            )}
                        </IconBox>
                        <Name $active={item.id === selectedCategoryId} $isScrolled={isScrolled}>
                            {item.title}
                        </Name>
                    </Card>
                ))}
            </Track>

            <SearchContainer $isVisible={isSearchActive}>
                <SearchInputWrapper>
                    <SearchIcon>
                        <FontAwesomeIcon icon={faSearch} />
                    </SearchIcon>
                    <SearchInput
                        ref={searchInputRef}
                        type="text"
                        placeholder={isVoucherFridge ? "Sök spelare eller produkter..." : "Sök efter produkter..."}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        onFocus={() => setIsSearchFocused(true)}
                        onBlur={() => setIsSearchFocused(false)}
                        aria-label={isVoucherFridge ? "Sök spelare" : "Sök produkter"}
                    />
                    {showSearchClear && (
                        <ClearButton onClick={clearSearch} aria-label="Rensa sökning">
                            <FontAwesomeIcon icon={faTimes} />
                        </ClearButton>
                    )}
                </SearchInputWrapper>
            </SearchContainer>
        </BarWrap>
    );
};

export default HorizontalCategoryList;

const SURFACE = "#f6f7fa";
const BORDER = "#e7ebf0";
const ACTIVE_SHADOW = "0 6px 20px rgba(0, 0, 0, 0.15)";
const INACTIVE_SHADOW = "0 1px 4px rgba(18,20,28,0.06)";

const BarWrap = styled.div`
    width: 100%;
    background: ${({ $isScrolled }) => ($isScrolled ? "rgba(246, 247, 250, 0.98)" : SURFACE)};
    padding: ${({ $isScrolled }) => ($isScrolled ? "8px 0" : "6px 0 10px")};
    border-bottom: 1px solid #eef2f6;
    //position: ${({ $isScrolled }) => ($isScrolled ? "fixed" : "relative")};
    position: sticky;
    //top: ${({ $isScrolled }) => ($isScrolled ? "var(--top-menu-h, 0px)" : "auto")};
    top:0;
    left: 0;
    right: 0;

    z-index: ${({ $isScrolled }) => ($isScrolled ? 2000 : 1)}; /* under TopMenu(1100), above content */
    transition: all 0.5s ease-in-out;
    box-shadow: ${({ $isScrolled }) => ($isScrolled ? "0 2px 15px rgba(0, 0, 0, 0.1)" : "none")};
    backdrop-filter: ${({ $isScrolled }) => ($isScrolled ? "blur(10px)" : "none")};
`;

const Track = styled.div`
    display: flex;
    flex-wrap: nowrap; /* ensure single row to overflow horizontally */
    justify-content: flex-start;
    gap: ${({ $isScrolled }) => ($isScrolled ? "8px" : "12px")};
    padding: 0 16px;
    overflow-x: auto; /* enable horizontal scrolling */
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x; /* improve horizontal swipe on touch devices */
    overscroll-behavior-x: contain; /* prevent parent from hijacking horizontal scroll */
    scrollbar-width: none; /* Firefox */
    cursor: grab;
    transition: gap 0.5s ease-in-out;

    &:active { cursor: grabbing; }

    &::-webkit-scrollbar {
        height: 0;
        display: none;
    }
`;

const SearchContainer = styled.div`
    padding: ${({ $isVisible }) => ($isVisible ? "8px 16px 8px" : "0 16px")};
    max-height: ${({ $isVisible }) => ($isVisible ? "60px" : "0px")};
    opacity: ${({ $isVisible }) => ($isVisible ? "1" : "0")};
    overflow: hidden;
    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: ${({ $isVisible }) => ($isVisible ? "auto" : "none")};
`;

const SearchInputWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
`;

const SearchInput = styled.input`
    width: 100%;
    height: 40px;
    padding: 0 45px 0 40px;
    border: 1px solid ${BORDER};
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    font-size: 16px;
    color: #374151;
    outline: none;
    transition: all 0.3s ease;
    -webkit-appearance: none;
    appearance: none;
    -webkit-tap-highlight-color: transparent;

    &::placeholder { color: #9ca3af; }

    &:focus {
        border-color: #6b7280;
        background: rgba(255, 255, 255, 1);
        box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.1);
    }
`;

const SearchIcon = styled.div`
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 16px;
    pointer-events: none;
    z-index: 1;
`;

const ClearButton = styled.button`
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 26px;
    height: 26px;
    border: none;
    background: #f3f4f6;
    border-radius: 50%;
    color: #6b7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    transition: all 0.3s ease;

    &:hover {
        background: #e5e7eb;
        color: #374151;
    }

    &:active {
        transform: translateY(-50%) scale(0.9);
    }
`;

const Card = styled.button`
    flex: 0 0 auto;
    width: ${({ $size }) => `${$size}px`};
    min-height: ${({ $size }) => `${$size}px`};
    /* allow the card to grow in height to accommodate long titles */
    height: auto;
    border-radius: ${({ $isScrolled }) => ($isScrolled ? "10px" : "14px")};
    background: ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.85)")};
    backdrop-filter: blur(8px);
    box-shadow: ${({ $active }) => ($active ? ACTIVE_SHADOW : INACTIVE_SHADOW)};
    display: grid;
    grid-template-rows: ${({ $size }) => `${Math.floor($size * 0.6)}px 1fr`};
    align-items: center;
    justify-items: center;
    padding: ${({ $isScrolled }) => ($isScrolled ? "4px" : "8px")};
    cursor: pointer;
    opacity: ${({ $isVisible }) => ($isVisible ? "1" : "0")};
    visibility: ${({ $isVisible }) => ($isVisible ? "visible" : "hidden")};
    transform: ${({ $active }) => ($active ? "scale(1.05) translateY(-2px)" : "scale(1)")};
    transform-origin: center center;
    transition:
            width 0.5s ease-in-out,
            /* height becomes auto; keep smoothness by animating min-height when needed */
            min-height 0.3s ease,
            opacity 0.4s ease,
            visibility 0.4s ease,
            border-radius 0.5s ease-in-out,
            padding 0.5s ease-in-out,
            transform 0.3s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.95);
        transform: ${({ $active }) => ($active ? "scale(1.05) translateY(-2px)" : "scale(1.02) translateY(-1px)")};
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.12);
    }

    &:active {
        transform: ${({ $active }) => ($active ? "scale(1.03) translateY(-1px)" : "scale(0.98)")};
    }
`;

const IconBox = styled.div`
    width: 100%;
    height: ${({ $size }) => `${Math.floor($size * 0.6)}px`};
    border-radius: ${({ $isScrolled }) => ($isScrolled ? "6px" : "10px")};
    background: #ffffff;
    display: grid;
    place-items: center;
    overflow: hidden;
    //box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

// const IconImg = styled.img`
//   max-width: ${({ $isScrolled }) => ($isScrolled ? "60%" : "80%")};
//   max-height: ${({ $isScrolled }) => ($isScrolled ? "60%" : "80%")};
//   object-fit: contain;
//   transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
// `;
const IconImg = styled.img`
    max-width: 80%;
    max-height: 80%;
    object-fit: contain;
`;


const IconContainer = styled.div`
    font-size: 20px;
    color: ${({ $active }) => ($active ? "#1f2937" : "#6b7280")};
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);

    ${Card}:hover & {
        color: #374151;
        transform: scale(1.1);
    }
`;

const Name = styled.div`
    font-size: 11px;
    font-weight: ${({ $active }) => ($active ? "600" : "500")};
    color: ${({ $active }) => ($active ? "#1f2937" : "#6b7280")};
    letter-spacing: 0.02em;
    text-align: center;
    padding: ${({ $isScrolled }) => ($isScrolled ? "2px 2px 0" : "4px 4px 0")};
    width: 100%;
    /* allow wrapping so full names are visible */
    white-space: normal;
    word-break: break-word;
    overflow: visible;
    text-overflow: clip;
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    line-height: 1.15;
`;

const Skeleton = styled.div`
    width: ${({ $size }) => `${$size}px`};
    height: ${({ $size }) => `${$size}px`};
    border-radius: ${({ $isScrolled }) => ($isScrolled ? "10px" : "14px")};
    background: linear-gradient(90deg, #f3f4f6 0%, #eceff3 50%, #f3f4f6 100%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite ease;
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);

    @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
    }
`;

const ErrorText = styled.div`
    font-size: 14px;
    text-align: center;
    color: red;
`;